import { LOCALES, useI18n, useT } from '@kovix/shell-kit/i18n';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { signOut } from '@/app/auth/auth';
import { useSession } from '@/app/auth/session.store';
import { ShellIcon } from './NavIcon';
import { initials, shellText } from './shellText';

/**
 * Thẻ hồ sơ trên topbar (mockup v152 `.tenant-pill` + `toggleAccountDropdown`): avatar chữ tắt + tên + "vai trò · tenant" + ⌄.
 * Bấm mở menu tài khoản: hồ sơ cá nhân / bảo mật / nhật ký đăng nhập / thiết bị (`/account?tab=…`), trung tâm thông báo,
 * chọn NGÔN NGỮ (`useI18n().setLocale` → localStorage `kovix.locale`) và ĐĂNG XUẤT — hai chức năng trước nằm rời trên topbar.
 * Đổi mật khẩu/MFA vẫn ở Keycloak Account Console (mở từ `/account`), không có form mật khẩu trong SPA.
 */
export function AccountMenu({
  roleLabel,
  roleNames,
  jobTitle,
}: {
  roleLabel: string;
  roleNames: string[];
  jobTitle: string | null;
}) {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const session = useSession();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const name = session.user?.name ?? '…';
  const sub = [roleLabel, session.tenantName].filter(Boolean).join(' · ');

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => setOpen(false);
  return (
    <div ref={root} className="kx-shell-account">
      <button
        ref={button}
        type="button"
        className="kx-tenant-pill kx-shell-pill"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="kx-account-menu"
        aria-label={`${name} ${sub} — ${shellText('accountMenu', locale)}`}
        title={`${shellText('accountMenu', locale)}${session.tenantId ? ` · ${session.tenantId}` : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="av" aria-hidden="true">
          {initials(session.user?.name)}
        </span>
        <span className="kx-shell-pill-copy">
          <span className="tn">{name}</span>
          <span className="tr">{sub}</span>
        </span>
        <ShellIcon name="chevronDown" size={15} className="kx-shell-pill-caret" />
      </button>
      {open ? (
        <div className="kx-shell-acm" id="kx-account-menu" role="dialog" aria-label={t('shell.account')}>
          <div className="kx-shell-acm-head">
            <span className="kx-shell-acm-av" aria-hidden="true">
              {initials(session.user?.name)}
            </span>
            <div className="kx-shell-acm-who">
              <b>{name}</b>
              {session.user?.email ? <small>{session.user.email}</small> : null}
              <small>{[jobTitle, session.tenantName].filter(Boolean).join(' · ')}</small>
              <div className="kx-shell-acm-badges">
                {roleNames.slice(0, 3).map((r) => (
                  <span key={r} className="kx-shell-acm-badge">
                    {r}
                  </span>
                ))}
                {roleNames.length > 3 ? <span className="kx-shell-acm-badge">+{roleNames.length - 3}</span> : null}
                <span className="kx-shell-acm-badge grey">
                  {shellText(session.mode === 'platform' ? 'platformWorkspace' : 'tenantWorkspace', locale)}
                </span>
              </div>
            </div>
          </div>
          <div className="kx-shell-acm-body">
            <Link className="kx-shell-acm-item" to="/account" search={{}} onClick={close}>
              <ShellIcon name="user" size={17} />
              <span>
                <b>{shellText('profile', locale)}</b>
                <small>{shellText('profileHint', locale)}</small>
              </span>
            </Link>
            <Link className="kx-shell-acm-item" to="/account" search={{ tab: 'security' }} onClick={close}>
              <ShellIcon name="shield" size={17} />
              <span>
                <b>{shellText('security', locale)}</b>
              </span>
            </Link>
            <Link className="kx-shell-acm-item" to="/account" search={{ tab: 'history' }} onClick={close}>
              <ShellIcon name="history" size={17} />
              <span>
                <b>{shellText('loginHistory', locale)}</b>
              </span>
            </Link>
            <Link className="kx-shell-acm-item" to="/account" search={{ tab: 'devices' }} onClick={close}>
              <ShellIcon name="device" size={17} />
              <span>
                <b>{shellText('devices', locale)}</b>
              </span>
            </Link>
            {session.hasModule('notify') ? (
              <Link className="kx-shell-acm-item" to="/notifications/inbox" search={{}} onClick={close}>
                <ShellIcon name="bell" size={17} />
                <span>
                  <b>{shellText('notifyCenter', locale)}</b>
                </span>
              </Link>
            ) : null}
            <label className="kx-shell-acm-item kx-shell-acm-lang" htmlFor="kx-shell-locale">
              <ShellIcon name="globe" size={17} />
              <span>
                <b>{t('shell.language')}</b>
              </span>
              <select id="kx-shell-locale" value={locale} onChange={(e) => setLocale(e.target.value as typeof locale)}>
                {LOCALES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="kx-shell-acm-foot">
            <button type="button" className="kx-btn kx-btn-ghost kx-btn-sm" onClick={close}>
              {t('common.close')}
            </button>
            <button type="button" className="kx-btn kx-btn-primary kx-btn-sm" onClick={() => void signOut()}>
              <ShellIcon name="logout" size={15} /> {t('shell.signOut')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
